"use client";

import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment } from "react";
import CrossIcon from "@/assets/icons/cross";
import clsx from "clsx";
import { IconButton } from "../icon-button";

const sizes = {
  xsmall: "sm:max-w-sm",
  small: "sm:max-w-lg",
  medium: "sm:max-w",
  large: "sm:max-w-[836px]",
  xlarge: "sm:max-w-5xl",
};

interface ModalProps extends React.PropsWithChildren {
  isOpen?: boolean;
  onClose?: () => void;
  withCloseButton?: boolean;
  size?: keyof typeof sizes;
  fixedButton?: boolean;
  disableCloseOnOverlayClick?: boolean;
}

export const Modal = ({
  isOpen,
  onClose,
  withCloseButton,
  children,
  size = "small",
  fixedButton,
  disableCloseOnOverlayClick = false,
}: ModalProps) => (
  <Transition appear show={isOpen} as={Fragment}>
    <Dialog
      as="div"
      className="relative z-50"
      unmount
      onClose={() =>
        !!onClose && !disableCloseOnOverlayClick ? onClose() : null
      }
    >
      <Transition.Child
        as={Fragment}
        enter="ease-out duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-md" />
      </Transition.Child>

      <div className="fixed xl:inset-0 w-full bottom-0 left-0 xl:overflow-y-auto">
        <div className="flex min-h-full items-center justify-center md:p-0 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="xl:opacity-0 xl:translate-y-10 xl:scale-95 translate-y-full"
            enterTo="xl:opacity-100 translate-y-0 scale-100"
            leave="ease-in duration-200"
            leaveFrom="xl:opacity-100 xl:scale-100 "
            leaveTo="xl:opacity-0 xl:scale-95 xl:translate-y-10 translate-y-full"
          >
            <Dialog.Panel
              className={clsx(
                "w-full transform overflow-x-hidden xl:max-h-none max-h-[80vh] overflow-y-auto xl:rounded-3xl rounded-t-3xl bg-white dark:bg-dark bg-opacity-80 dark:bg-opacity-50 backdrop-blur-lg text-left rtl:text-right transition-all",
                sizes[size],
              )}
            >
              {withCloseButton && (
                <div
                  className={clsx(
                    "xl:top-4 xl:right-5 rtl:left-5 max-w-max rtl:right-auto z-10",
                    fixedButton
                      ? "xl:absolute fixed right-2 top-2 rtl:left-2 rtl:right-auto"
                      : "absolute top-4 right-4 rtl:left-4 rtl:right-auto",
                  )}
                >
                  <IconButton rounded size="small" onClick={onClose}>
                    <CrossIcon />
                  </IconButton>
                </div>
              )}
              {children}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </div>
    </Dialog>
  </Transition>
);
